import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { SITE, IMG } from "../data/siteData";

const FAQS = [
  {
    q: 'What is "balanced training"?',
    a: "Simply put, balanced training is based on the idea that dogs learn from a variety of factors including: reinforcement, consequences, emotions and environment. Balanced training uses the scientific principles of operant and classical conditioning, in combination with various training tools from treats to slip collars, e-collars and prong collars to shape and reinforce behaviours we want to see more of and correct behaviours we want to see less of. This system of training has proven to be the most effective way for any dog to learn, from your pet dog, to world class sport dogs and military working dogs alike.",
  },
  {
    q: "What tools do we use?",
    a: "When it comes to dog training, it is important to remember that one size does not fit all; having a large tool box is key. We use a variety of tools to assist with training and motivating dogs, from toys, play, food, and praise, to flat collars, remote training collars and pinch collars. The tools used will depend on what each dog responds best to as well as the goals you wish to accomplish!",
  },
  {
    q: "Will a prong collar hurt my dog?",
    a: "No! Prong collars have a bad reputation. Do they look scary? Absolutely. However, they are an excellent tool for clear guidance and control that will not hurt your dog when used correctly. They allow the handler to give the dog clear information with pressure that is evenly applied across the neck, both to control and guide movement, and stop unwanted behaviours such as leash pulling and reactive behaviour. We exclusively use Herm Sprenger collars to ensure quality and consistency.",
  },
  {
    q: "More on e-collars…",
    a: "E-collars have come a long way since their invention — modern e-collar training is nothing like it used to be! E-collars are the clearest, and most effective way to control your dog, especially in off-leash scenarios.\n\nWe exclusively use E-Collar Technologies products. Their collars have an easy to use system with 100 levels of stimulation allowing us to adjust the level used based on each individual dog and scenario.\n\nWhy we use them? E-collars open a new, clear, consistent line of communication between you and your dog even when there is no leash involved! It is the only tool giving you the possibility to reliably recall your dog at great distances 100% of the time.\n\nAll owners and dogs have the opportunity to see great success with e-collar training when used correctly and in an educated and humane manner.\n\nKeep in mind, though, the tool is only as strong as the training behind it! We will never recommend anyone use an e-collar without professional guidance.",
  },
  {
    q: 'Will my dog be "fixed" after training?',
    a: 'We will never claim to "fix" dogs. What we do work to fix is relationships — the relationship between you and your dog and between your dog and the world around them. We will be there to give you the tools, knowledge and support to reach those goals. The long-term success of the training, though, really comes down to you and your commitment to putting in the work based on realistic goals and implementing the information and skills we teach!',
  },
  {
    q: "What if I am unhappy with the results of training?",
    a: "Due to the many variables involved to create a well-behaved, balanced dog, including owner commitment, consistency and follow-through, it would be unethical for us to offer a 100% guarantee. That being said, we only choose to work with the most dedicated and diligent owners; in our experience, no matter how severe the issues, we consistently see breakthrough results and amazing transformations. We work hand in hand as a team in your dog's rehabilitation. This journey of teamwork and collaboration begins before we even meet your dog, moves through the training process, and continues long after your dog has gone home.",
  },
  {
    q: "What is Training Without Conflict®?",
    a: "Training Without Conflict® is a system of dog training developed by world renowned dog trainer and 2x World IGP Champion Ivan Balabanov that focuses on training dogs through biological/genetic fulfilment (play), and the understanding and application of demonstrable and observable behavioural sciences.\n\nTraining Without Conflict® Certified Dog Trainers have a deep understanding of ethology, neuroscience and behaviourism and they possess the skills to apply this knowledge to help even the most difficult training and behaviour cases.",
  },
];

export default function Faq() {
  return (
    <div data-testid="faq-page">
      <SEO
        title="FAQs | Balanced Training, E-Collars & Tools | Chinook K9 Calgary"
        description="Answers about balanced dog training, e-collars, prong collars and our methods. Chinook K9 — Training Without Conflict® certified trainers serving Calgary & surrounding areas."
        path="/faqs"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a.replace(/\n+/g, " ") },
            })),
          })}
        </script>
      </Helmet>

      <section className="relative min-h-[50vh] flex items-end overflow-hidden" data-testid="faq-hero">
        <img src={IMG.privateTraining} alt="Chinook K9 trainer working with a dog" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/25 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 pt-44 w-full">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4">Tools & Methods</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white">
              Frequently Asked Questions
            </h1>
            <p className="mt-5 text-white/80 max-w-2xl leading-relaxed">
              Everything you want to know about how we train — balanced training, e-collars, prong collars, and what to expect from your dog's program.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24" data-testid="faq-list">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="bg-[#121212] border border-white/10 rounded-xl px-6 data-[state=open]:border-brand/50 transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger
                    className="font-heading text-base sm:text-lg font-bold text-white hover:text-brand hover:no-underline py-6 text-left"
                    data-testid={`faq-question-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70 leading-relaxed text-sm sm:text-base pb-6" data-testid={`faq-answer-${i}`}>
                    {f.a.split("\n\n").map((p, j) => (
                      <p key={j} className={j > 0 ? "mt-4" : ""}>{p}</p>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#121212] border-t border-white/10 py-20" data-testid="faq-cta">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Still have questions?</h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">Call or email us anytime — we're happy to walk you through our tools and methods before you book.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="faq-contact-cta" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                Contact Us <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} data-testid="faq-call-cta" className="border border-white/25 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
                <Phone size={16} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
